import React from "react";
import {
    Box,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    Tooltip,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import type { NavKey } from "../types";
import { ACCOUNT_ROUTES } from "../account.routes";
import { useAccount } from "../context/AccountContext";
import { QUIZ } from "@/theme/quizTokens";

type Props = {
    nav: NavKey;
    setNav: (k: NavKey) => void;
    collapsed?: boolean;
    onToggleCollapse?: () => void;

    brandTitle?: string;
    brandLogoUrl?: string;
};

export function NavContent({
    nav,
    setNav,
    collapsed = false,
    onToggleCollapse,
    brandTitle = "Paws on Plates",
    brandLogoUrl = "https://cdn.shopify.com/s/files/1/0946/0622/9844/files/Paws_on_Plates_Tertiary_Logo_Brown.svg?v=1744270930",
}: Props) {
    const { isDevAccessible } = useAccount();

    // Filter out dev tab if user doesn't have access
    const visibleRoutes = ACCOUNT_ROUTES.filter(route => {
        if (route.key === "dev") return isDevAccessible;
        return true;
    });

    return (
        <Box
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                position: "relative", // ✅ anchor the floating pill
                overflow: "visible", // ✅ allow pill to sit outside sidebar
            }}
        >
            {/* ✅ Floating collapse pill (outside scroll area) */}
            {onToggleCollapse && (
                <Box
                    sx={{
                        position: "absolute",
                        top: 14, // aligns nicely with top brand row
                        right: -14, // pushes outside sidebar edge
                        zIndex: 5000,
                    }}
                >
                    <Tooltip title={collapsed ? "Expand sidebar" : "Collapse sidebar"} placement="right">
                        <IconButton
                            size="small"
                            onClick={onToggleCollapse}
                            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
                            sx={{
                                width: 30,
                                height: 30,
                                borderRadius: 999,
                                background: QUIZ.color.warmWhite,
                                border: "1px solid rgba(0,0,0,.10)",
                                boxShadow: "0 6px 18px rgba(0,0,0,.10)",
                                "&:hover": { background: "rgba(250,245,235,.95)" },
                            }}
                        >
                            {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                        </IconButton>
                    </Tooltip>
                </Box>
            )}

            {/* Top brand area */}
            <Box
                sx={{
                    py: collapsed ? 2 : 3,
                    px: collapsed ? 1 : 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Box
                    component="img"
                    src={brandLogoUrl}
                    alt={brandTitle}
                    sx={{
                        width: collapsed ? 40 : 120,
                        objectFit: "contain",
                        flexShrink: 0,
                    }}
                />
            </Box>

            {/* Nav items (scrollable) */}
            <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", p: collapsed ? 1 : 2 }}>
                <List disablePadding sx={{ display: "grid", gap: 0.5 }}>
                    {visibleRoutes.map((it) => {
                        const active = nav === it.key;
                        return (
                            <ListItemButton
                                key={it.key}
                                onClick={() => setNav(it.key)}
                                sx={{
                                    borderRadius: "8px",
                                    background: active ? QUIZ.color.blue : "transparent",
                                    px: collapsed ? 1.25 : 1.5,
                                    justifyContent: collapsed ? "center" : "flex-start",
                                    minHeight: 46,
                                    transition: "all 200ms ease",
                                    "&:hover": {
                                        background: active ? QUIZ.color.blue : QUIZ.color.cream,
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 22,
                                        display: "grid",
                                        placeItems: "center",
                                        color: active ? QUIZ.color.ink : QUIZ.color.grey,
                                        mr: collapsed ? 0 : 1.5,
                                    }}
                                >
                                    {it.icon}
                                </Box>

                                {!collapsed && (
                                    <>
                                        <ListItemText
                                            primary={it.label}
                                            primaryTypographyProps={{
                                                fontFamily: QUIZ.font.body,
                                                fontWeight: 500,
                                                color: active ? QUIZ.color.ink : QUIZ.color.grey,
                                                fontSize: 16,
                                                letterSpacing: "-0.16px",
                                            }}
                                        />
                                        {!active && (
                                            <ChevronRightIcon
                                                sx={{ fontSize: 16, color: QUIZ.color.grey, opacity: 0.7 }}
                                            />
                                        )}
                                    </>
                                )}
                            </ListItemButton>
                        );
                    })}
                </List>
            </Box>
        </Box>
    );
}